import { stateT } from "../../types";
import { IObject, IProjectCategory, SearchProjectI } from "../uiTypes";
import { createSelector } from "@reduxjs/toolkit";
import { getObjects } from "./objectSelectors";
import { getCurrentFolder } from "./objectSelectors";
import { getPathToRoot } from "./objectSelectors";

export const getSearchData = (state: stateT) => {
  return state.ui.searchData;
};

export const getSearchValue = (state: stateT): string => {
  const searchData = getSearchData(state);
  return searchData.searchValue;
};

export const getIsSearching = (state: stateT): boolean => {
  const searchValue = getSearchValue(state);
  return searchValue.length > 0;
};

export const getIsNoResultsFound = (state: stateT): boolean => {
  const searchData = getSearchData(state);
  const isSearching = getIsSearching(state);
  return (
    isSearching &&
    searchData.searchFolders.length === 0 &&
    searchData.searchProjects.length === 0 &&
    (!searchData.searchProducts || searchData.searchProducts.length === 0)
  );
};

export const getSearchFolders = (state: stateT) => {
  const searchData = getSearchData(state);
  return searchData.searchFolders;
};

// Memoized: avoid returning a new array unless searchData.searchProjects changes
export const getSearchProjects = createSelector([getSearchData], searchData =>
  searchData.searchProjects.map(item => item.project)
);

export const getSearchProjectPathsByProjectId =
  (id: number) => (state: stateT) => {
    const searchData = getSearchData(state);

    const searchProject = searchData.searchProjects.find(
      item => item.project.id === id
    );

    if (searchProject) {
      return searchProject.pathToProject;
    }

    return [];
  };

export const getSearchProducts = (state: stateT) => {
  const searchData = getSearchData(state);
  return searchData.searchProducts || [];
};

export const getObjectsBySearchValue =
  (searchValue: string) =>
  (state: stateT): IObject[] => {
    const currentFolder = getCurrentFolder(state);

    // Exclude the current folder itself; search only within its children subtree.
    const objects = currentFolder
      ? currentFolder.children || []
      : getObjects(state);
    const result: IObject[] = [];

    const searchInObject = (obj: IObject) => {
      if (obj.name.toLowerCase().includes(searchValue.toLowerCase())) {
        result.push(obj);
      }

      if (obj.children && obj.children.length > 0) {
        obj.children.forEach(child => searchInObject(child));
      }
    };

    objects.forEach(obj => searchInObject(obj));
    return result;
  };

// Search within the current folder by product names.
// Uses products fetched into currentCategoryFolder if present, otherwise falls back to productsCache.
export const getAllProjectsBySearchValue =
  (searchValue: string) =>
  (state: stateT): SearchProjectI[] => {
    const currentFolder = state.ui.currentCategoryFolder;
    const result: SearchProjectI[] = [];

    const search = searchValue.trim().toLowerCase();
    if (!currentFolder || !search.length) return result;

    const pathToRootIds = (() => {
      const path = getPathToRoot(state);
      if (Array.isArray(path) && path.length) return path.map(p => p.id);
      return [currentFolder.id];
    })();

    const projects: IProjectCategory[] = currentFolder.projects || [];

    // If we have products, match by product names, otherwise fallback to project names
    projects.forEach(project => {
      if ((project.name || "").toLowerCase().includes(search)) {
        result.push({ project, pathToProject: pathToRootIds });
      }
    });

    return result;
  };
